import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Eye, EyeSlash, ShieldCheck, ArrowLeft } from '@phosphor-icons/react';
import Seo from '../components/Seo';

export default function LoginPage() {
  const { login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const emailRef = useRef(null);
  const otpRef = useRef(null);
  const submittingRef = useRef(false);
  const verifyingRef = useRef(false);
  const [email, setEmail] = useState(() => localStorage.getItem('roofmate_remember_email') || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem('roofmate_remember_email'));
  const [deactivatedMsg, setDeactivatedMsg] = useState('');
  // OTP state
  const [otpStep, setOtpStep] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [resending, setResending] = useState(false);
  // Single-session takeover prompt — shown to device B when the user is
  // already signed in on device A.
  const [takeoverPrompt, setTakeoverPrompt] = useState(null); // { message } | null
  const [kickedMsg, setKickedMsg] = useState('');

  useEffect(() => {
    emailRef.current?.focus();
    const msg = localStorage.getItem('roofmate_deactivated_msg');
    if (msg) {
      setDeactivatedMsg(msg);
      localStorage.removeItem('roofmate_deactivated_msg');
    }
    // Banner shown to a device that was kicked because another device
    // signed in — set by AuthContext on 401 SESSION_INVALIDATED.
    if (localStorage.getItem('roofmate_session_kicked')) {
      setKickedMsg('You were signed out because another login was detected on this account.');
      localStorage.removeItem('roofmate_session_kicked');
    }
  }, []);

  useEffect(() => {
    if (otpStep && otpRef.current) otpRef.current.focus();
  }, [otpStep]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Hard guard against rapid double-submits. setLoading is async, so two submit
    // events fired back-to-back (double-click, Enter+Click, etc.) can both bypass the
    // disabled-button check. The ref flips synchronously and blocks the second call.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError('');
    setLoading(true);
    if (remember) {
      localStorage.setItem('roofmate_remember_email', email);
    } else {
      localStorage.removeItem('roofmate_remember_email');
    }
    try {
      const result = await login(email, password);
      if (result.requires_takeover_confirmation) {
        setTakeoverPrompt({ message: result.message });
      } else if (result.requires_otp) {
        setOtpStep(true);
        setOtpEmail(result.email);
        setOtpMessage(result.message);
      } else if (!result.success) {
        setError(result.error);
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  // User confirmed on the "signed in on another device" modal — resubmit
  // the login with force_takeover=true so the backend proceeds past the
  // gate (mints a new sid → old device gets 401 SESSION_INVALIDATED on
  // its next request).
  const handleConfirmTakeover = async () => {
    setTakeoverPrompt(null);
    setLoading(true);
    try {
      const result = await login(email, password, { forceTakeover: true });
      if (result.requires_otp) {
        setOtpStep(true);
        setOtpEmail(result.email);
        setOtpMessage(result.message);
      } else if (!result.success) {
        setError(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setError('');
    setLoading(true);
    try {
      const result = await verifyOtp(otpEmail, otpCode);
      if (!result.success) setError(result.error);
    } finally {
      setLoading(false);
      verifyingRef.current = false;
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    const result = await resendOtp(otpEmail);
    if (result.success) {
      setOtpMessage(result.message || 'New code sent!');
    } else {
      setError(result.error);
    }
    setResending(false);
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setOtpCode('');
    setError('');
    setOtpMessage('');
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center" data-testid="login-page">
      <Seo title="Sign in" description="Sign in to your RoofMate account." path="/login" noIndex />
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1547717826-01f4fb5496ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3VzZSUyMGFlcmlhbCUyMHJvb2Z8ZW58MHx8fHwxNzc2MjMyODcxfDA&ixlib=rb-4.1.0&q=85"
          alt="Aerial rooftop photograph used as RoofMate sign-in background"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/45" />
      </div>

      <div className="relative z-10 w-full max-w-[440px] mx-4 animate-fadeIn">
        <div className="bg-card rounded-xl shadow-2xl p-8 sm:p-10">
          <div className="flex items-center mb-7 cursor-pointer" onClick={() => navigate('/')} data-testid="login-logo">
            <img src="/logo-full.png" alt="RoofMate" className="h-[2.4rem] object-contain" />
          </div>

          {!otpStep ? (
            <>
              <h1 className="text-2xl font-semibold mb-1" data-testid="login-heading">Welcome to RoofMate!</h1>
              <p className="text-muted-foreground text-sm mb-6">Please sign-in to your account and start measuring</p>

              {deactivatedMsg && (
                <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-sm rounded-md" data-testid="deactivated-message">
                  {deactivatedMsg}
                </div>
              )}

              {kickedMsg && (
                <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300 text-sm rounded-md" data-testid="session-kicked-message">
                  {kickedMsg}
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md" data-testid="login-error">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[13px] font-medium">Email</Label>
                  <Input ref={emailRef} id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@roofmate.app" required autoFocus className="h-[38px] rounded-md" data-testid="login-email-input" />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-[13px] font-medium">Password</Label>
                    <a href="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot Password?</a>
                  </div>
                  <div className="relative">
                    <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required className="h-[38px] rounded-md pr-10" data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-password-visibility">
                      {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox id="remember" checked={remember} onCheckedChange={(checked) => setRemember(!!checked)} data-testid="remember-checkbox" />
                  <Label htmlFor="remember" className="text-[13px] font-normal text-muted-foreground cursor-pointer">Remember me</Label>
                </div>

                <Button type="submit" disabled={loading} className="w-full h-[38px] rounded-md font-medium text-sm" data-testid="login-submit-btn">
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <button onClick={handleBackToLogin} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors" data-testid="otp-back-btn">
                <ArrowLeft size={14} /> Back to login
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <ShieldCheck size={24} className="text-primary" weight="duotone" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold" data-testid="otp-heading">Verify Your Identity</h1>
                  <p className="text-muted-foreground text-xs mt-0.5">Two-factor authentication</p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-5">
                We sent a 6-digit verification code to <span className="font-semibold text-foreground">{otpEmail}</span>
              </p>

              {otpMessage && !error && (
                <div className="mb-4 p-3 bg-primary/5 border border-primary/15 text-primary text-sm rounded-md" data-testid="otp-message">{otpMessage}</div>
              )}

              {error && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md" data-testid="otp-error">{error}</div>
              )}

              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-medium">Verification Code</Label>
                  <Input
                    ref={otpRef}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    required
                    className="h-[42px] rounded-md text-center text-lg font-bold tracking-[0.3em]"
                    data-testid="otp-code-input"
                  />
                </div>

                <Button type="submit" disabled={loading || otpCode.length !== 6} className="w-full h-[38px] rounded-md font-medium text-sm" data-testid="otp-verify-btn">
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>
              </form>

              <div className="text-center mt-5">
                <p className="text-xs text-muted-foreground">
                  Didn't receive the code?{' '}
                  <button onClick={handleResend} disabled={resending} className="text-primary hover:underline font-medium" data-testid="otp-resend-btn">
                    {resending ? 'Sending...' : 'Resend Code'}
                  </button>
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {takeoverPrompt && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          role="dialog"
          aria-modal="true"
          data-testid="takeover-modal"
        >
          <div className="bg-card rounded-lg shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300 inline-flex items-center justify-center shrink-0">
                <ShieldCheck size={20} weight="bold" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-semibold" data-testid="takeover-modal-title">Signed in on another device</div>
                <p className="text-sm text-muted-foreground mt-1" data-testid="takeover-modal-message">
                  {takeoverPrompt.message}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setTakeoverPrompt(null)}
                data-testid="takeover-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmTakeover}
                disabled={loading}
                data-testid="takeover-confirm"
              >
                Continue &amp; sign out other device
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
